<template>
  <div :class="$style.statsTabsContainer">
    <!-- 드롭다운 버튼: 스크롤에 포함되지 않음 (항상 왼쪽 고정) -->
    <div :class="$style.dropdownWrap" ref="dropdownContainerRef">
      <button ref="dropdownToggleRef" :class="$style.dropdownToggle" @click.stop="toggleDropdown" type="button">
        <img src="@/assets/images/dehaze.png" alt="통계 메뉴" :class="$style.dehazeIcon" />
      </button>
      <Teleport to="body">
        <div v-if="showDropdown" ref="dropdownMenuRef" :class="$style.dropdownMenu" :style="dropdownStyle">
          <button v-for="tab in tabs" :key="tab.route" :class="[
            $style.dropdownItem,
            { [$style.activeDropdownItem]: isActiveTab(tab.route) },
          ]" @click="selectFromDropdown(tab.route)">
            <span :class="$style.dropdownItemIcon">{{ tab.icon }}</span>
            {{ tab.label }}
          </button>
        </div>
      </Teleport>
    </div>

    <!-- 탭 버튼만 스크롤 -->
    <div :class="$style.tabsWrapper">
      <button v-for="tab in tabs" :key="tab.route" :class="[
        $style.tabButton,
        { [$style.activeTab]: isActiveTab(tab.route) },
      ]" @click="navigateToTab(tab.route)">
        <span :class="$style.tabIcon">{{ tab.icon }}</span>
        <span :class="$style.tabLabel">{{ tab.label }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useUserStore } from "/src/store/user.js";

export default {
  name: "StatsTabs",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const showDropdown = ref(false);
    const dropdownContainerRef = ref(null);
    const dropdownToggleRef = ref(null);
    const dropdownMenuRef = ref(null);
    const dropdownStyle = ref({ left: "0px", top: "0px" });

    const allTabs = [
      {
        route: "/growth-overview",
        label: "성장 대시보드",
        icon: "📈",
        adminOnly: true,
      },
      {
        route: "/visitor-stats",
        label: "방문자 통계",
        icon: "📊",
      },
      {
        route: "/traffic-stats",
        label: "유입 경로",
        icon: "🚀",
      },
      {
        route: "/ad-management",
        label: "광고 관리",
        icon: "📢",
        adminOnly: true,
      },
      {
        route: "/user-stats",
        label: "사용자 통계",
        icon: "👥",
      },
      {
        route: "/live-stats",
        label: "라이브 통계",
        icon: "🎤",
      },
      {
        route: "/artist-stats",
        label: "아티스트 통계",
        icon: "🎵",
      },
      {
        route: "/venue-stats",
        label: "장소별 통계",
        icon: "🏢",
      },
      {
        route: "/like-stats",
        label: "찜 통계",
        icon: "💖",
      },
      {
        route: "/report-management",
        label: "신고 관리",
        icon: "🚨",
        adminOnly: true,
      },
      {
        route: "/alarm-management",
        label: "알람 관리",
        icon: "🔔",
        adminOnly: true,
      },
      {
        route: "/activity-stats",
        label: "활동 통계",
        icon: "📊",
        allowedEmail: "karmuie3@daum.net",
      },
      {
        route: "/core-user-stats",
        label: "코어유저관리",
        icon: "🎯",
        allowedEmail: "karmuie3@daum.net",
      },
    ];

    // 권한에 따른 탭 필터링
    const tabs = allTabs.filter(
      (tab) => {
        // adminOnly 탭은 관리자만
        if (tab.adminOnly && userStore.auth !== "admin") return false;
        // allowedEmail이 설정된 탭은 해당 이메일만
        if (tab.allowedEmail && userStore.email !== tab.allowedEmail) return false;
        return true;
      }
    );

    const isActiveTab = (tabRoute) => {
      return route.path === tabRoute;
    };

    const navigateToTab = (tabRoute) => {
      if (route.path !== tabRoute) {
        router.push(tabRoute);
      }
    };

    const DROPDOWN_MIN_WIDTH = 180;
    const VIEWPORT_PAD = 12;

    const updateDropdownPosition = () => {
      const btn = dropdownToggleRef.value;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // 뷰포트 안에 들어오도록 left 보정 (왼쪽/오른쪽 밖으로 나가지 않게)
      let left = rect.left;
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
      if (left + DROPDOWN_MIN_WIDTH > vw - VIEWPORT_PAD) {
        left = vw - DROPDOWN_MIN_WIDTH - VIEWPORT_PAD;
      }
      let top = rect.bottom + 8;
      const maxHeight = Math.min(0.7 * vh, 400);
      // 아래가 잘리면 버튼 위로 열기
      if (top + maxHeight > vh - VIEWPORT_PAD) {
        top = rect.top - 8 - maxHeight;
        if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;
      }
      dropdownStyle.value = {
        left: `${left}px`,
        top: `${top}px`,
      };
    };

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
      if (showDropdown.value) {
        nextTick(updateDropdownPosition);
      }
    };

    watch(showDropdown, (open) => {
      if (open) nextTick(updateDropdownPosition);
    });

    const selectFromDropdown = (tabRoute) => {
      navigateToTab(tabRoute);
      showDropdown.value = false;
    };

    const handleClickOutside = (event) => {
      const container = dropdownContainerRef.value;
      const menu = dropdownMenuRef.value;
      const target = event.target;
      if (container?.contains(target) || menu?.contains(target)) return;
      showDropdown.value = false;
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });
    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      tabs,
      isActiveTab,
      navigateToTab,
      showDropdown,
      dropdownContainerRef,
      dropdownToggleRef,
      dropdownMenuRef,
      dropdownStyle,
      toggleDropdown,
      selectFromDropdown,
    };
  },
};
</script>

<style module>
.statsTabsContainer {
  display: flex;
  align-items: center;
  background: white;
  margin-bottom: 20px;
  border-radius: 12px 12px 0 0;
  overflow: visible;
}

/* 드롭다운 버튼: 스크롤 영역 밖, 항상 왼쪽 고정 */
.dropdownWrap {
  flex-shrink: 0;
  position: relative;
  margin-right: 8px;
  padding-left: 2px;
}

/* 탭 버튼만 스크롤 */
.tabsWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.tabsWrapper::-webkit-scrollbar {
  display: none;
}

.dropdownToggle {
  height: 34px;
  width: 30px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: "NanumSquareRoundR", sans-serif;
  border: none;
}

.dropdownToggle:hover {
  background: #f8fafc;
}

.dehazeIcon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  filter: brightness(0) invert(0.4);
  /* #666 */
}

.dropdownMenu {
  position: fixed;
  /* left, top은 인라인으로 버튼 위치 기준 설정 */
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1100;
  min-width: 180px;
  max-height: 70vh;
  overflow-y: auto;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdownItem {
  width: 100%;
  padding: 10px 14px;
  background: white;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dropdownItem:last-child {
  border-bottom: none;
}

.dropdownItem:hover {
  background: #f8fafc;
  color: #334155;
}

.dropdownItem.activeDropdownItem {
  background: #1e293b;
  color: white;
  font-weight: bold;
}

.dropdownItemIcon {
  font-size: 0.9rem;
  display: inline-block;
  flex-shrink: 0;
}

/* 게시판 필터처럼: 아이콘+글자 한 줄, 둥근 버튼 */
.tabButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #f1f5f9;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.tabButton:hover {
  background: #f8fafc;
  color: #334155;
}

.tabButton.activeTab {
  background: #1e293b;
  color: white;
  border-color: #1e293b;
}

.tabIcon {
  font-size: 0.85rem;
  line-height: 1;
  display: inline-block;
}

.tabLabel {
  font-size: inherit;
  letter-spacing: 0.2px;
}

/* ========================================
   반응형 디자인
   - Large: 1025px ~ (기본 스타일)
   - Medium: 481px ~ 1024px
   - Small: ~ 480px
   ======================================== */

/* Medium (481px ~ 1024px) */
@media (max-width: 1024px) {
  .statsTabsContainer {
    margin-bottom: 15px;
    border-radius: 10px 10px 0 0;
  }

  .tabsWrapper {
    padding: 0 8px;
    -webkit-overflow-scrolling: touch;
  }

  .tabButton {
    padding: 8px 16px;
    font-size: 0.78rem;
    gap: 5px;
  }

  .tabIcon {
    font-size: 0.8rem;
  }
}

/* Small (~ 480px) */
@media (max-width: 480px) {
  .statsTabsContainer {
    margin-bottom: 12px;
    border-radius: 8px 8px 0 0;
  }

  .tabsWrapper {
    padding: 0 6px;
  }

  .tabButton {
    padding: 6px 12px;
    font-size: 0.72rem;
    gap: 4px;
  }

  .tabIcon {
    font-size: 0.75rem;
  }
}
</style>
